import {z} from 'zod';

import {
  FileDataSchema,
  Money,
  MoneySchema,
  TemplateListItemResponseBodySchema,
  BigDecimalSchema,
  Currency,
  ContractInformationResponseBodySchema,
  IdentityCardResponseBodySchema,
  PersonResponseBodySchema,
} from '@dms/api/shared';

export type GetOrderApiRes = OrderResponseBody;

export type GetOrderApiArg = {
  orderId: string;
  checkoutId: string;
};
export type DeletePaymentFileApiArg = {
  orderId: string;
  checkoutId: string;
  paymentId: string;
  fileId: string;
};
export type DeletePaymentFileApiRes = unknown;

export const TemplatesListItemResponseBodySchema = z.object({
  type: z.string(),
  templates: z.array(TemplateListItemResponseBodySchema),
});
export type TemplatesListItemResponseBody = z.infer<typeof TemplatesListItemResponseBodySchema>;
export type SplitCheckoutOrderApiResponse = /** status 200  */ CheckoutResponseBody;
export type SplitCheckoutOrderApiArg = {
  checkoutId: string;
  orderId: string;
  orderSplitRequestBody: OrderSplitRequestBody;
};
export const OrderDiscriminatorSchema = z.enum([
  'PURCHASE',
  'SALE',
  'PURCHASE_BROKERAGE_SALE',
  'PURCHASE_BROKERAGE_FEES',
]);

export type OrderDiscriminator = z.infer<typeof OrderDiscriminatorSchema>;
export const CheckoutDeputyPersonResponseBodySchema = z.object({
  customerPerson: PersonResponseBodySchema,
  selectedIdentityCards: z.array(IdentityCardResponseBodySchema),
});
export type CheckoutDeputyPersonResponseBody = z.infer<
  typeof CheckoutDeputyPersonResponseBodySchema
>;
export const CheckoutContractInformationBodySchema = z.object({
  customerContractInformation: ContractInformationResponseBodySchema,
  selectedIdentityCards: z.array(IdentityCardResponseBodySchema),
  deputyPersons: z.array(CheckoutDeputyPersonResponseBodySchema),
});
export type CheckoutContractInformationBody = z.infer<typeof CheckoutContractInformationBodySchema>;
export const PaymentDiscriminatorSchema = z.enum(['DEPOSIT', 'BALANCE', 'PURCHASE']);

export type PaymentDiscriminator = z.infer<typeof PaymentDiscriminatorSchema>;
export const PaymentMethodSchema = z.enum(['CASH', 'BANK_TRANSFER', 'PAYMENT_CARD', 'OFFSET']);

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export const TenantBankAccountSchemaCheckout = z.object({
  bankName: z.string(),
  accountName: z.string(),
  accountNumber: z.string(),
  isPrimary: z.boolean(),
  currencyCode: z.string().nullable(),
  swiftBic: z.string().nullable(),
  iban: z.string().nullable(),
});

export type TenantBankAccountCheckout = z.infer<typeof TenantBankAccountSchemaCheckout>;
export const PaymentStateSchema = z.enum(['CONCEPT', 'PENDING', 'PAID']);

export type PaymentState = z.infer<typeof PaymentStateSchema>;
export const OrderPaymentResponseBodySchema = z.object({
  id: z.string(),
  paymentDiscriminator: PaymentDiscriminatorSchema,
  paymentMethod: PaymentMethodSchema,
  amount: MoneySchema.nullable(),
  payDate: z.string().nullable(),
  issuedOn: z.string().nullable(),
  dueDate: z.string().nullable(),
  customerBankAccountId: z.string().nullable(),
  tenantBankAccount: TenantBankAccountSchemaCheckout.nullable(),
  cashRegisterId: z.string().nullable(),
  paymentState: PaymentStateSchema,
  files: z.array(FileDataSchema),
  comment: z.string().nullable(),
  invoices: z.array(FileDataSchema),
  proformaInvoices: z.array(FileDataSchema),
  taxableDocuments: z.array(FileDataSchema),
  cashReceipts: z.array(FileDataSchema),
  correctiveTaxDocuments: z.array(FileDataSchema),
  invoiceId: z.string().nullable(),
  proformaInvoiceId: z.string().nullable(),
  taxDocumentId: z.string().nullable(),
  internalInvoiceDocuments: z.array(FileDataSchema),
  internalCorrectiveTaxDocuments: z.array(FileDataSchema),
  createCashRegisterDocument: z.boolean().nullable(),
  cashRegisterDocumentId: z.string().nullable(),
  exchangeRate: z.string().nullable(),
  foreignCurrencyAmount: MoneySchema.nullable(),
  supplierInvoiceNumber: z.string().nullable(),
  dateOfTaxableSupply: z.string().nullable(),
});
export type OrderPaymentResponseBody = z.infer<typeof OrderPaymentResponseBodySchema>;
export const CheckoutPriceResponseBodySchema = z.object({
  withVat: MoneySchema,
  withoutVat: MoneySchema.nullish(),
  vat: MoneySchema.nullish(),
  vatRate: BigDecimalSchema.nullish(),
});
export type CheckoutPriceResponseBody = z.infer<typeof CheckoutPriceResponseBodySchema>;
export const OrderItemResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  price: CheckoutPriceResponseBodySchema,
  deductible: z.boolean(),
  itemContextId: z.string().nullable(),
});
export type OrderItemResponseBody = z.infer<typeof OrderItemResponseBodySchema>;
export const TypeOfSaleSchema = z.enum(['inland', 'inEurope', 'outOfEurope']);

export type TypeOfSale = z.infer<typeof TypeOfSaleSchema>;
export const OrderAdditionalCustomerResponseBodySchema = z.object({
  id: z.string(),
  customerId: z.string(),
  additionalCustomerType: z.enum(['operator', 'recipient']),
  contractInformationId: z.string(),
});

export type OrderAdditionalCustomerResponseBody = z.infer<
  typeof OrderAdditionalCustomerResponseBodySchema
>;
export const OrderResponseBodySchema = z.object({
  id: z.string(),
  orderDiscriminator: OrderDiscriminatorSchema,
  depositAllowed: z.boolean(),
  contractInformation: CheckoutContractInformationBodySchema,
  payments: z.array(OrderPaymentResponseBodySchema),
  items: z.array(OrderItemResponseBodySchema),
  totalPrice: CheckoutPriceResponseBodySchema,
  files: z.array(FileDataSchema),
  typeOfSale: TypeOfSaleSchema,
  addPurchasePaymentAllowed: z.boolean(),
  additionalCustomers: z.array(OrderAdditionalCustomerResponseBodySchema),
  foreignCurrencyPayment: z.boolean(),
  currency: z.string().nullable(),
  exchangeRateRatioAmount: z.number().nullable(),
  discountForDisabled: z.boolean().optional(),
});
export type OrderResponseBody = z.infer<typeof OrderResponseBodySchema>;
export const ItemsContextSchema = z.object({
  orderId: z.string().optional(),
  itemId: z.string().optional(),
  entityId: z.string().optional(),
  entityClass: z.string().optional(),
});

const TaxDocumentSchema = z.object({
  taxDocumentId: z.string(),
  taxDocumentType: z.string(),
  orderId: z.string(),
  paymentId: z.string(),
  itemsContext: z.array(ItemsContextSchema),
});

export type TaxDocumentCheckout = z.infer<typeof TaxDocumentSchema>;
export const CheckoutResponseBodySchema = z.object({
  id: z.string(),
  orders: z.array(OrderResponseBodySchema),
  editContractInformationAllowed: z.boolean(),
  taxDocuments: z.array(TaxDocumentSchema),
});
export type CheckoutResponseBody = z.infer<typeof CheckoutResponseBodySchema>;
export type OrderSplitRequestBody = {
  itemContextIds: string[];
};
export type MergeCheckoutOrderApiResponse = /** status 200  */ CheckoutResponseBody;
export type MergeCheckoutOrderApiArg = {
  checkoutId: string;
  orderId: string;
  targetOrderId: string;
};
export type PatchCheckoutOrderForeignCurrencyPaymentApiResponse =
  /** status 200  */ OrderResponseBody;
export type PatchCheckoutOrderForeignCurrencyPaymentApiArg = {
  checkoutId: string;
  orderId: string;
  orderForeignCurrencyPaymentRequestBody: OrderForeignCurrencyPaymentRequestBody;
};
export type OrderForeignCurrencyPaymentRequestBody = {
  foreignCurrencyPayment: boolean;
  currency: Currency | null;
  exchangeRateRatioAmount: number | null;
};
export type WithdrawalPaymentApiArg = {
  orderId: string;
  checkoutId: string;
  paymentId: string;
  prescriptionId: string;
};
export type WithdrawalPaymentApiRes = unknown;

export type OrderPaymentRecordPaidRequestBodyV2 = {
  payDate: string;
  amount: string;
  currency: string;
  paymentMethod: 'CASH' | 'BANK_TRANSFER' | 'PAYMENT_CARD' | 'OFFSET';
  cashRegisterId?: string | null;
  createCashRegisterDocument?: boolean | null;
  tenantBankAccount?: TenantBankAccountCheckout | null;
};

export type OrderPaymentRecordPaidTransactionResponseBodyV3ReadCheckout = {
  /** Created payment transaction id */
  transactionId: string;
  /** Invoicing document id related to created transaction */
  documentId: string;
  checkoutId: string;
  orderId: string;
  paymentId: string;
  paymentDirection: 'incoming' | 'outgoing';
};

export type RecordPaidCheckoutOrderPaymentApiResponse = /** status 200  */ OrderPaymentResponseBody;
export type RecordPaidCheckoutOrderPaymentApiArg = {
  checkoutId: string;
  orderId: string;
  paymentId: string;
  orderPaymentRecordPaidRequestBody: OrderPaymentRecordPaidRequestBody;
};
export type OrderPaymentRecordPaidRequestBody = {
  payDate: string | null;
};
export type CreateAdditionalCustomerRequestBody = {
  customerId: string;
  additionalCustomerType: 'operator' | 'recipient';
};
export type CreateCheckoutOrderAdditionalCustomerApiResponse =
  /** status 201  */ CheckoutResponseBody;
export type CreateCheckoutOrderAdditionalCustomerApiArg = {
  checkoutId: string;
  orderId: string;
  createAdditionalCustomerRequestBody: CreateAdditionalCustomerRequestBody;
};
export type AllowDepositApiArg = {checkoutId: string; orderId: string};
export type ForbitDepositApiArg = {checkoutId: string; orderId: string};
export type AllowDepositApiRes = {
  id: string;
  orders: OrderResponseBody[];
  editContractInformationAllowed: boolean;
  taxDocuments: TaxDocumentCheckout[];
};
export type ForbitDepositApiRes = {
  id: string;
  orders: OrderResponseBody[];
  editContractInformationAllowed: boolean;
  taxDocuments: TaxDocumentCheckout[];
};

export type WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiResponse =
  /** status 200  */ OrderPaymentResponseBody;
export type WithdrawCheckoutOrderPaymentByCashRegisterDocumentApiArg = {
  cashRegisterDocumentId: string;
};
export type DeleteCheckoutOrderAdditionalCustomerApiResponse = unknown;
export type DeleteCheckoutOrderAdditionalCustomerApiArg = {
  checkoutId: string;
  orderId: string;
  additionalCustomerId: string;
};
export type PatchCheckoutOrderAdditionalCustomerApiResponse =
  /** status 200  */ CheckoutResponseBody;
export type PatchCheckoutOrderAdditionalCustomerApiArg = {
  checkoutId: string;
  orderId: string;
  additionalCustomerId: string;
  patchAdditionalCustomerRequestBody: PatchAdditionalCustomerRequestBody;
};
export type PatchAdditionalCustomerRequestBody = {
  additionalCustomerType: AdditionalCustomerType;
  contractInformationId: string;
};

export type InternalInvoiceDocumentRequestBody = {
  templateId: string;
  checkoutId: string;
  orderId: string;
  paymentId: string;
};
export type AdditionalCustomerType = 'operator' | 'recipient';

export type ProformaInvoiceSettingsEnum = 'without-proforma-invoice' | 'with-proforma-invoice';
export type BusinessCaseProformaInvoiceSettingResponseBody = {
  id: string;
  branchId: string | null;
  proformaInvoiceSettings: ProformaInvoiceSettingsEnum;
  isSystem: boolean;
};

export type GetCheckoutApiResponse = /** status 200  */ CheckoutResponseBody;
export type GetCheckoutApiArg = {
  checkoutId: string;
};
export type GetBusinessCaseProformaInvoiceSettingApiArg = {id: string};
export type GetListBusinessCaseProformaInvoiceSettingApiArg = void;
export type GetListBusinessCaseProformaInvoiceSettingApiResponse =
  BusinessCaseProformaInvoiceSettingResponseBody[];
export type UpdateBusinessCaseInvoiceSettingApiResponse =
  BusinessCaseProformaInvoiceSettingResponseBody;
export type GetBusinessCaseProformaInvoiceSettingApiResponse =
  BusinessCaseProformaInvoiceSettingResponseBody;
export type DeleteBusinessCaseProformaInvoiceSettingApiResponse = void;
export type CreateBusinessCaseProformaInvoiceSettingApiResponse =
  BusinessCaseProformaInvoiceSettingResponseBody;
export type CreateBusinessCaseProformaInvoiceSettingApiArg = {
  branchId: string;
  proformaInvoiceSettings: ProformaInvoiceSettingsEnum;
};
export type UpdateBusinessCaseInvoiceSettingApiArg = {
  id: string;
  branchId: string | null;
  proformaInvoiceSettings: ProformaInvoiceSettingsEnum;
};
export type DeleteBusinessCaseProformaInvoiceSettingApiArg = {
  id: string;
};
export type ChangeTypeOfSaleCheckoutOrderApiResponse = /** status 200  */ CheckoutResponseBody;
export type ChangeTypeOfSaleCheckoutOrderApiArg = {
  checkoutId: string;
  orderId: string;
  orderTypeOfSaleRequestBody: {
    typeOfSale: TypeOfSale;
  };
};

export type SetDiscountForDisabledApiResponse = /** status 204 No Content */ void;
export type SetDiscountForDisabledApiArg = {
  businessCaseId: string;
  orderId: string;
  setDiscountForDisabledRequestBody: {
    discountForDisabled: boolean;
  };
};

export type PutCheckoutDocumentsApiRes = /** status 200  */ CheckoutResponseBody;
export type PutCheckoutDocumentsApiArg = {
  checkoutId: string;
  orderId: string;
  templateIds: string[];
};
export type GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiResponse =
  /** status 200  */ OffsettingOfLiabilitiesAndReceivablesResponseBody;
export type GetCheckoutOffsettingOfLiabilitiesAndReceivablesApiArg = {
  checkoutId: string;
};

export type OffsettingOfLiabilitiesAndReceivablesResponseBody = {
  claim: OrderItemResponseBody[];
  liabilities: OrderItemResponseBody[];
  credit: Money;
};

export type CheckoutSubEntityClass =
  | 'DEFAULT'
  | 'PURCHASE_CONTRACT'
  | 'SALE_CONTRACT'
  | 'PURCHASE_BROKERAGE_CONTRACT'
  | 'SALE_BROKERAGE_CONTRACT';

export type CreateInternalCorrectiveTaxDocumentDocumentApiResponse =
  /** status 200  */ CheckoutResponseBodyReadCheckout;
export type CheckoutResponseBodyReadCheckout = {
  id: string;
  orders: OrderResponseBody[];
  editContractInformationAllowed: boolean;
  taxDocuments: TaxDocumentCheckout[];
};
export type CreateInternalCorrectiveTaxDocumentDocumentApiArg = {
  internalCorrectiveTaxDocumentRequestBody: InternalCorrectiveTaxDocumentRequestBody;
};
export type InternalCorrectiveTaxDocumentRequestBody = {
  templateId: string;
  checkoutId: string;
  orderId: string;
  paymentId: string;
  taxDocumentId?: string;
};

export type CreateDueDateSettingApiResponse = /** status 201  */ DueDateSettingResponseBody;
export type CreateDueDateSettingApiArg = {
  branchId: string;
  proformaInvoiceDueDate: string;
  invoiceDueDate: string;
};
export type DeleteDueDateSettingApiResponse = unknown;
export type DeleteDueDateSettingApiArg = {
  id: string;
};
export type GetDueDateSettingApiResponse = /** status 200  */ DueDateSettingResponseBody;
export type GetDueDateSettingApiArg = {
  id: string;
};
export type UpdateDueDateSettingApiResponse = /** status 200  */ DueDateSettingResponseBody;
export type UpdateDueDateSettingApiArg = {
  id: string;
  branchId: string | null;
  proformaInvoiceDueDate: string;
  invoiceDueDate: string;
};
export type GetListDueDateSettingApiResponse = /** status 200  */ DueDateSettingResponseBody[];
export type GetListDueDateSettingApiArg = void;
export type DueDateSettingResponseBody = {
  id: string;
  branchId: string | null;
  proformaInvoiceDueDate: string;
  invoiceDueDate: string;
  isSystem: boolean;
};
